// floatingSlot — 計測フローティング・スロット方式の純ロジック
// (docs/_wip/modern-layout.md §9.7 engine 永続化「DOM 配置 = 計測フローティング・
//  スロット方式」)。
//
// 背景: レイアウト切替で canvas がクリア/desync するのを根絶するため、engine/canvas
// を切替の上位 (RoomLayout 直下) に hoist して 1 個だけ永続化する。永続 `CanvasStage`
// は RoomLayout 直下に `position: absolute` で置き、各 chrome (Modern/Classic) は canvas
// 領域を **空スロット div** にする。CanvasStage は「アクティブなスロットの矩形」を
// ResizeObserver で計測して absolute で重ねることで、chrome の見た目を一切変えずに
// canvas を chrome の上に正確に乗せる。
//
// この純ロジックは「DOM rect 計測の数値変換」だけを担い (= DOM/React 非依存)、
// node テストで固定できるようにする。実 DOM 計測・追従は `useSlotRect` (React hook)
// が `getBoundingClientRect` / `ResizeObserver` で行い、ここに数値だけを渡す。

/** 矩形 (viewport 座標系、`getBoundingClientRect` 互換の left/top/width/height)。 */
export interface SlotRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * `CanvasStage` を RoomLayout 直下に絶対配置するための、スロット矩形 →
 * stage の絶対座標 (= RoomLayout 原点基準) 変換。
 *
 * `CanvasStage` は RoomLayout (= `position: relative` の親) 直下に
 * `position: absolute` で置かれる。`getBoundingClientRect` は viewport 基準の
 * 値を返すので、stage の `left/top` は **スロット rect から親 rect を引いた相対値**
 * になる。width/height はそのままスロットの実寸を使う (canvas バッファは固定 px だが、
 * 囲うコンテナは stage 側の scroll/overlay が担うので stage の枠はスロットに一致させる)。
 *
 * @param slot   アクティブなスロット div の `getBoundingClientRect`
 * @param parent stage の絶対配置基準となる親 (RoomLayout root) の `getBoundingClientRect`
 */
export const computeStageRect = (slot: SlotRect, parent: SlotRect): SlotRect => ({
  left: slot.left - parent.left,
  top: slot.top - parent.top,
  width: slot.width,
  height: slot.height,
});

/**
 * 2 つの矩形が「実質同じ」か (= 再配置の setState を抑止するための比較)。
 *
 * ResizeObserver / rAF は同じ rect を高頻度で報告しうるので、変化が無い更新で
 * React state を回さないよう、サブピクセルの揺れを `epsilon` で吸収して等価判定する。
 * 既定 0.5px = レイアウト上の有意差未満は「変化なし」とみなす。
 */
export const slotRectEquals = (a: SlotRect, b: SlotRect, epsilon = 0.5): boolean =>
  Math.abs(a.left - b.left) < epsilon &&
  Math.abs(a.top - b.top) < epsilon &&
  Math.abs(a.width - b.width) < epsilon &&
  Math.abs(a.height - b.height) < epsilon;
